/**
 * @prettier
 * @flow
 */
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { paymentLinkApi } from 'src/api/paymentLinkApi';
import { BackButton } from 'src/components/buttons/BackButton';
import { CopyToClipboardButton } from 'src/components/buttons/CopyToClipboardButton';
import { Form } from 'src/components/form/Form';
import { FormCheckbox } from 'src/components/form/FormCheckbox';
import { FormContactDetailsField } from 'src/components/form/FormContactDetailsField';
import { FormCurrencyAmountField } from 'src/components/form/FormCurrencyAmountField';
import { FormMsiField } from 'src/components/form/FormMsiField';
import { FormShippingDetailsField } from 'src/components/form/FormShippingDetailsField';
import { FormTextField } from 'src/components/form/FormTextField';
import type { EmailAddress, PaymentLinkId } from 'src/types/Id';
import type { PaymentLinkVm } from 'src/types/PaymentLinkVm';
import { alertKnownErrorOrSomethingWentWrong } from 'src/utils/alert/alertKnownErrorOrSomethingWentWrong';
import { formatAsReadableDateTime } from 'src/utils/date/formatAsReadableDateTime';
import { parsePaymentLinkId } from 'src/utils/id/parsePaymentLinkId';
import { translate } from 'src/utils/translate/translate';

export function PaymentLinkPage({ paymentLinkId }: Props) {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [paymentLink, setPaymentLink] = useState(((null: any): PaymentLinkVm));
    const url = useRef(null);

    const form = useForm<FormType>();

    useEffect(() => {
        load();
    }, []);

    const load = async () => {
        setLoading(true);
        if (!paymentLinkId) return;
        const response = await paymentLinkApi.getPaymentLink({ paymentLinkId: parsePaymentLinkId(paymentLinkId) });
        if (!response.ok) {
            setLoading(false);
            alertKnownErrorOrSomethingWentWrong(response);
            return;
        }
        setLoading(false);
        setPaymentLink(response.data);
        form.setValue('customerName', response.data.customerName);
        form.setValue('contact', {
            email: response.data.email,
            phoneNumber: response.data.phoneNumber,
        });
        form.setValue('askForShippingAddress', response.data.askForShippingAddress);
        form.setValue('shippingAddress', response.data.shippingAddress);
        form.setValue('reason', response.data.reason);
        form.setValue('total', response.data.total);
        form.setValue('msi', response.data.msi?.toString());
        form.setValue('paymentLinkStatus', translate(`PaymentLinkStatuses.${response.data.paymentLinkStatus}`));
        form.setValue('expiresAt', formatAsReadableDateTime(response.data.expiresAt));
        form.setValue('url', response.data.url);
    };

    return (
        <Paper className={classes.paper}>
            <div className={classes.backButtonContainer}>
                <BackButton />
            </div>
            <Typography variant='h4' align='center' className={classes.header}>
                {translate('Payment Link')}
            </Typography>
            {/*<Typography variant="subtitle1" align="center" className={classes.header}>*/}
            {/*    {translate('Hello @customer, you received a payment request from @company', { customer: paymentLink?.customerName, company: 'Let’s Eat' })}*/}
            {/*</Typography>*/}
            <Form form={form}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormTextField name='customerName' label={translate('Customer Name')} />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <FormTextField name='reason' label={translate('Payment Reason')} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormCurrencyAmountField name='total' label={translate('Total Amount')} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormTextField name='expiresAt' label={translate('Expires')} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormTextField name='paymentLinkStatus' label={translate('Status')} />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={3}>
                                <FormMsiField name='msi' label={translate('Months without Interest')} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <FormTextField
                            name='url'
                            ref={url}
                            label={translate('Payment Link Url')}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        <Box m={-1.5}>
                                            <CopyToClipboardButton getText={() => form.getValues('url')} />
                                        </Box>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormContactDetailsField name='contact' />
                    </Grid>
                    <Grid item xs={12}>
                        <FormCheckbox name='askForShippingAddress' label={translate('Ask for Shipping Address')} />
                    </Grid>
                    {paymentLink?.askForShippingAddress && (
                        <Grid item xs={12}>
                            <FormShippingDetailsField name='shippingAddress' />
                        </Grid>
                    )}
                </Grid>
            </Form>
        </Paper>
    );
}

const useStyles = makeStyles(theme => ({
    header: {
        paddingBottom: theme.spacing(4),
    },
    backButtonContainer: {
        position: 'absolute',
    },
    paper: {
        padding: theme.spacing(2),
        [theme.breakpoints.up(800 + theme.spacing(3) * 2)]: {
            padding: theme.spacing(3),
        },
    },
    buttonContainer: {
        paddingTop: theme.spacing(4),
    },
}));

type Props = {
    paymentLinkId?: PaymentLinkId,
};

type FormType = {|
    customerName: string,
    email: EmailAddress,
    reason: string,
    total: string,
    expiresInDays: string,
|};
